import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSnackbar from "components/SoftSnackbar";
import SearchableSelect from "components/SearchableSelect";
import SoftPagination from "components/SoftPagination";
import SoftSelect from "components/SoftSelect";

// Icons
import Icon from "@mui/material/Icon";
import {
  CircularProgress,
  Tooltip,
  Zoom
} from "@mui/material";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// API config
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import FlagLoader from "layouts/flagloader";

// InfographicBar Component - Medium Colors
const InfographicBar = ({ data, type, height = 28 }) => {
  const colorPalette = useMemo(() => [
    '#FFB74D', // Orange
    '#4FC3F7', // Light Blue
    '#81C784', // Green
    '#FF8A65', // Pinkish Orange
    '#9575CD', // Purple
    '#4DB6AC', // Teal
    '#FFD54F', // Yellow
    '#7986CB', // Indigo
    '#F06292', // Pink
    '#AED581', // Light Green
    '#64B5F6', // Blue
    '#FFA726', // Amber
    '#BA68C8', // Light Purple
    '#4DD0E1', // Cyan
    '#A1887F', // Brown
  ], []);

  const getColor = (index) => {
    return colorPalette[index % colorPalette.length];
  };

  if (!data || Object.keys(data).length === 0) {
    return (
      <Box sx={{
        height: `${height}px`,
        borderRadius: '4px',
        backgroundColor: 'rgba(0,0,0,0.04)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px dashed rgba(0,0,0,0.1)'
      }}>
        <Icon sx={{
          color: 'rgba(0,0,0,0.3)', fontSize: 14, display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: '0 !important'
        }}>
          {type === 'browser'
            ? 'language'
            : type === 'device'
              ? 'devices'
              : 'people'}

        </Icon>
      </Box>
    );
  }

  // Make sure data is valid object
  const entries = Object.entries(data || {});

  // Transform to unified structure
  const transformed = entries.map(([name, obj]) => {
    const hits = obj?.hits ?? obj ?? 0; // support old structure too
    const completes = obj?.completes ?? 0;

    return {
      name,
      hits,
      completes,
      total: hits
    };
  });

  // Sort based on hits
  const sortedEntries = transformed.sort((a, b) => b.hits - a.hits);

  // Calculate total hits
  const total = transformed.reduce((sum, item) => sum + item.hits, 0);

  const hasSingleEntry = sortedEntries.length === 1;

  const tooltipContent = (
    <Box sx={{
      p: 1.2,
      bgcolor: '#111',
      borderRadius: '8px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.15)',

      width: 'max-content',          // ✅ content decides width
      maxWidth: '90vw',               // ✅ responsive safety
    }}>


      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1,
        pb: 1,
        borderBottom: '1px solid rgba(255,255,255,0.15)'
      }}>
        <Icon sx={{
          fontSize: 14,
          color: '#1abc9c',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: '0 !important'
        }}>
          {type === 'browser' ? 'language' : 'devices'}
        </Icon>

        <Box>
          <SoftTypography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff' }}>
            {type === 'browser'
              ? 'Browser Distribution'
              : type === 'device'
                ? 'Device Distribution'
                : 'User Distribution'}

          </SoftTypography>

          <SoftTypography
            variant="caption"
            sx={{
              color: '#ffffff !important',
              fontSize: '12px',
              fontWeight: 600,
              display: 'block',
              mt: 0.3,
              textShadow: '0px 0px 4px rgba(0,0,0,0.6)' // better visibility
            }}
          >
            Total: {total.toLocaleString()} hits
          </SoftTypography>

          <SoftTypography
            variant="caption"
            sx={{
              color: '#1abc9c',
              fontSize: '12px',
              fontWeight: 700,
              display: 'block',
              mt: 0.3
            }}
          >
            Total completes: {sortedEntries.reduce((sum, item) => sum + (item.completes || 0), 0)}
          </SoftTypography>


        </Box>
      </Box>

      {/* List */}
      <Box sx={{ maxHeight: 180, overflowY: 'auto', pr: 0.5 }}>
        {sortedEntries.map((item, index) => {
          const color = getColor(index + (type === 'device' ? 5 : 0));

          // ✅ USER: ONLY NAME
          if (type === 'user') {
            return (
              <Box
                key={item.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.6,
                  whiteSpace: "nowrap"
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: color
                  }}
                />
                <SoftTypography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#fff"
                  }}
                >
                  {index + 1}. {item.name}
                </SoftTypography>
              </Box>
            );
          }

          // ✅ Browser / Device: AS IT IS
          const percentage = total > 0 ? ((item.hits / total) * 100).toFixed(1) : 0;

          return (
            <Box key={item.name} sx={{ display: "flex", justifyContent: "space-between", mb: 0.7, whiteSpace: "nowrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ width: 9, height: 9, borderRadius: "50%", background: color }} />

                {/* Name */}
                <SoftTypography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#fff",
                    mr: 0.8          // ✅ IMPORTANT GAP after name
                  }}
                >
                  {item.name}
                </SoftTypography>
              </Box>


              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SoftTypography sx={{ fontSize: "12px", color: "#fff", fontWeight: 700 }}>
                  {item.hits}
                </SoftTypography>

                <SoftTypography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    bgcolor: "#1abc9c",
                    px: 0.6,
                    py: 0.2,
                    borderRadius: "6px",
                    color: "#fff"
                  }}
                >
                  C: {item.completes}
                </SoftTypography>

                <SoftTypography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    bgcolor: color,
                    px: 0.6,
                    py: 0.2,
                    borderRadius: "6px",
                    color: "#111"
                  }}
                >
                  {percentage}%
                </SoftTypography>
              </Box>
            </Box>
          );
        })}

      </Box>
    </Box>
  );


  if (hasSingleEntry) {
    const item = sortedEntries[0];
    const color = getColor(type === 'device' ? 5 : 0);


    return (
      <Tooltip
        title={tooltipContent}
        arrow
        TransitionComponent={Zoom}
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: 'transparent !important',
              p: 0,
              m: 0,
              boxShadow: 'none',
              maxWidth: 'none'
            }
          },
          arrow: {
            sx: {
              color: '#2c3e50 !important',
              "&::before": {
                border: 'none'
              }
            }
          }
        }}
      >

        <Box sx={{
          height: `${height}px`,
          borderRadius: '4px',
          backgroundColor: color,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.1)',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 3px 8px rgba(0,0,0,0.15)'
          }
        }}>
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, transparent 40%)',
          }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      TransitionComponent={Zoom}
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'transparent !important',
            p: 0,
            m: 0,
            boxShadow: 'none'
          }
        },
        arrow: {
          sx: {
            color: '#2c3e50 !important',
            "&::before": {
              border: 'none'
            }
          }
        }
      }}
    >

      <Box sx={{
        height: `${height}px`,
        borderRadius: '4px',
        overflow: 'hidden',
        display: 'flex',
        width: '100%',
        border: '1px solid rgba(0,0,0,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 3px 8px rgba(0,0,0,0.1)'
        }
      }}>
        {sortedEntries.map((item, index) => {
          const percentage = total > 0 ? (item.hits / total * 100) : 0;

          const color = getColor(index + (type === 'device' ? 5 : 0));

          if (percentage < 0.5) return null;

          return (
            <Box
              key={`${item.name}-${index}`}
              sx={{
                width: `${percentage}%`,
                height: '100%',
                backgroundColor: color,
                position: 'relative',
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 0.9,
                  filter: 'brightness(1.1)'
                },
                '&:first-of-type': {
                  borderTopLeftRadius: '4px',
                  borderBottomLeftRadius: '4px'
                },
                '&:last-of-type': {
                  borderTopRightRadius: '4px',
                  borderBottomRightRadius: '4px'
                }
              }}
            >
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 50%)',
              }} />

              {index > 0 && (
                <Box sx={{
                  position: 'absolute',
                  left: 0,
                  top: '15%',
                  bottom: '15%',
                  width: '1px',
                  backgroundColor: 'rgba(255,255,255,0.5)',
                }} />
              )}
            </Box>
          );
        })}

        <Box sx={{
          flex: 1,
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.03)',
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px'
        }} />
      </Box>
    </Tooltip>
  );
};

InfographicBar.propTypes = {
  data: PropTypes.object,
  type: PropTypes.oneOf(['browser', 'device', 'user']),
  height: PropTypes.number
};

InfographicBar.defaultProps = {
  data: {},
  type: 'browser',
  height: 28
};

// 🔥 Uniform Input Height Styling
const inputStyle = {
  "& .MuiOutlinedInput-root": {
    minHeight: "40px !important",
    maxHeight: "40px !important",
    fontSize: "13px",
    paddingRight: "8px",
    "& fieldset": {
      borderColor: "#ddd",
    },
    "& input": {
      padding: "8px !important"
    }
  }
};


function StudiesHits() {
  const [hitsData, setHitsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [show, setShow] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);

  // ✅ NEW: Date filter states
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [fromData, setFromData] = useState(0);
  const [toData, setToData] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();

  const toggleSnackbar = () => setShow(!show);

  const fetchHitsData = async () => {
    setLoading(true);
    try {
      let queryParams = [`per_page=${perPage}`, `page=${currentPage}`];

      if (searchQuery.trim()) {
        queryParams.push(`q=${encodeURIComponent(searchQuery.trim())}`);
      }

      if (selectedClient && selectedClient.value) {
        queryParams.push(`client_id=${selectedClient.value}`);
      }

      // ✅ NEW: Add start_date and end_date parameters
      if (fromDate) {
        queryParams.push(`start_date=${fromDate}`);
      }

      if (toDate) {
        queryParams.push(`end_date=${toDate}`);
      }

      const queryString = queryParams.join('&');
      const url = `${API_URL}/studies/hits?${queryString}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success !== 1) {
        throw new Error(`${data.message}`);
      }

      const transformedData = data.data.map((item) => {
        const userData = {};
        (item.users || []).forEach(u => {
          userData[u.name] = {
            hits: u.hits,
            completes: u.completes
          };
        });
        const surveyName = item.survey_name || "N/A";
        const surveyKey = item.survey_key || "N/A";

        return {
          quest_code: (
            <SoftTypography
              variant="body2"
              sx={{
                color: 'rgb(23, 193, 232)', // Changed color
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              {surveyName}
            </SoftTypography>
          ),
          survey_id: (
            <SoftTypography
              variant="body2"
              sx={{
                color: '#666',
                fontFamily: 'monospace',
                fontSize: '14px',
                bgcolor: '#f8f9fa',
                px: 1,
                py: 0.5,
                borderRadius: '4px',
                display: 'inline-block'
              }}
            >
              {surveyKey}
            </SoftTypography>
          ),
          account_name: (
            <SoftTypography variant="body2" sx={{ color: '#333', fontWeight: '500', fontSize: '14px' }}>
              {item.client_name || "Unknown"}
            </SoftTypography>
          ),
          country: (
            <Box sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: '12px',
              display: 'inline-block'
            }}>
              <SoftTypography variant="body2" sx={{ color: 'rgb(23, 193, 232)', fontWeight: '600', fontSize: '14px' }}>
                {item.country || "N/A"}
              </SoftTypography>
            </Box>
          ),
          browser: (
            <Box sx={{ minWidth: 250 }}>
              <InfographicBar data={item.browsers || {}} type="browser" height={26} />
            </Box>
          ),
          device: (
            <Box sx={{ minWidth: 250 }}>
              <InfographicBar data={item.devices || {}} type="device" height={26} />
            </Box>
          ),
          user: (
            <Box sx={{ minWidth: 250 }}>
              <InfographicBar
                data={userData}
                type="user"
                height={26}
              />
            </Box>
          )
        };
      });

      setHitsData(transformedData);
      setTotalRecords(data.total_records || data.data?.length || 0);

      setTotalPages(data.total_pages || Math.ceil((data.total_records || data.data.length) / perPage));
      setFromData(((currentPage - 1) * perPage) + 1);
      setToData(Math.min(currentPage * perPage, data.total_records || data.data.length));

    } catch (err) {
      console.error("Error fetching hits data:", err);
      setMessage(err.message || "Failed to load data");
      setShow(true);
      setHitsData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHitsData();
  }, [searchQuery, selectedClient, currentPage, perPage, fromDate, toDate]); // ✅ Added fromDate, toDate

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/clients?per_page=250`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.success === 1) {
        const clientOptions = data.clients.data.map((client) => ({
          value: client.id,
          label: client.name,
        }));
        setClients(clientOptions);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleClientChange = (selectedOption) => {
    setSelectedClient(selectedOption);
    setCurrentPage(1);
  };

  // ✅ NEW: Handle date changes
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      fetchHitsData();
    }
  };

  const handleRefresh = () => {
    fetchHitsData();
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    if (value === '') {
      setCurrentPage('');
      return;
    }
    const pageNumber = Number(value);

    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (searchQuery) params.append("q", searchQuery);
      if (selectedClient?.value) params.append("client_id", selectedClient.value);
      if (fromDate) params.append("start_date", fromDate);
      if (toDate) params.append("end_date", toDate);

      const url = `${API_URL}/studies/hits/export?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Accept": "text/csv"
        }
      });

      if (!response.ok) {
        throw new Error("CSV export failed");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `study_hits_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error(error);
      setMessage("Export failed!");
      setShow(true);
    } finally {
      setLoading(false);
    }
  };


  const tableColumns = [
    { name: "quest_code", align: "left", width: "130px" },
    { name: "survey_id", align: "left", width: "220px" },
    { name: "account_name", align: "left", width: "160px" },
    { name: "country", align: "center", width: "100px" },
    { name: "browser", align: "left", width: "280px" },
    { name: "device", align: "left", width: "280px" },
    { name: "user", align: "left", width: "280px" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color="primary"
        icon="notifications"
        title="Studies Hits"
        content={message ?? ''}
        open={show}
        close={toggleSnackbar}
        onClose={() => setShow(false)}
      />

      {/* Top Bar with Filter and Info */}

      {/* Filter Card */}
      <Card
        sx={{
          mb: 2,
          px: 2,
          py: 2,
          width: "100%",
          borderRadius: "8px",
        }}
      >
        <div className="row g-3 align-items-end">

          {/* Filter by Client */}
          <div className="col-12 col-sm-6 col-md-3">
            <SoftTypography
              variant="caption"
              fontWeight="600"
              sx={{ display: "block", mb: 0.7 }}
            >
              Filter by Client
            </SoftTypography>
            <SoftSelect
              name="client"
              options={clients}
              value={selectedClient}
              onChange={handleClientChange}
              size="medium"
              placeholder="Select Client"
              isMulti={false}
              isClearable={true}
            />



          </div>

          {/* Start Date */}
          <div className="col-12 col-sm-6 col-md-3">
            <SoftTypography
              variant="caption"
              fontWeight="600"
              sx={{ display: "block", mb: 0.7 }}
            >
              Start Date
            </SoftTypography>

            <SoftInput
              type="datetime-local"
              fullWidth
              value={fromDate}
              onChange={handleFromDateChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px !important",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiOutlinedInput-input": {
                  height: "42px !important",
                  padding: "0 12px !important",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          </div>

          {/* End Date */}
          <div className="col-12 col-sm-6 col-md-3">
            <SoftTypography
              variant="caption"
              fontWeight="600"
              sx={{ display: "block", mb: 0.7 }}
            >
              End Date
            </SoftTypography>

            <SoftInput
              type="datetime-local"
              fullWidth
              value={toDate}
              onChange={handleToDateChange}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px !important",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiOutlinedInput-input": {
                  height: "42px !important",
                  padding: "0 12px !important",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          </div>

          {/* Search Survey */}
          <div className="col-12 col-sm-6 col-md-3">
            <SoftTypography
              variant="caption"
              fontWeight="600"
              sx={{ display: "block", mb: 0.7 }}
            >
              Search Survey
            </SoftTypography>

            <SoftInput
              fullWidth
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "42px !important",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiOutlinedInput-input": {
                  height: "42px !important",
                  padding: "0 12px !important",
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          </div>

        </div>
      </Card>

      {/* Export Button Row */}
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mb={1.5}
        px={1}
      >
        <SoftButton
          color="success"
          variant="gradient"
          size="small"
          disabled={loading}
          startIcon={<Icon>download</Icon>}
          onClick={handleExport}
        >
          {loading ? "Processing..." : "Export CSV"}
        </SoftButton>
      </Box>

      {/* Showing Count and Pagination */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        px: 1
      }}>
        <SoftTypography variant="body2" color="text" sx={{ fontWeight: '500', fontSize: '14px' }}>
          <span style={{ color: "rgb(23, 193, 232)", fontWeight: 'bold' }}>Showing</span> {fromData} to {toData} of {totalRecords.toLocaleString()} <span style={{ color: "rgb(23, 193, 232)", fontWeight: 'bold' }}>entries</span>
        </SoftTypography>

        {/* Pagination Controls */}
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            flexWrap: 'nowrap',
            whiteSpace: 'nowrap',
            '& .MuiPaginationItem-root': {
              minWidth: '32px !important'
            }
          }}
        >

          <SoftPagination
            item
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            sx={{
              minWidth: '32px',
              height: '32px',
              fontSize: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              '&:not(:disabled):hover': {
                bgcolor: '#f8f9fa'
              }
            }}
          >
            <Icon sx={{ fontSize: 16 }}>first_page</Icon>
          </SoftPagination>

          <SoftPagination
            item
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            sx={{
              minWidth: '32px',
              height: '32px',
              fontSize: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              '&:not(:disabled):hover': {
                bgcolor: '#f8f9fa'
              }
            }}
          >
            <Icon sx={{ fontSize: 16 }}>chevron_left</Icon>
          </SoftPagination>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mx: 1
          }}>
            <SoftInput
              inputProps={{
                type: "number",
                min: 1,
                max: totalPages,
                style: {
                  textAlign: 'center',
                  fontSize: '14px',
                  padding: '4px 8px'
                }
              }}
              value={currentPage}
              onChange={handleInputChange}
              sx={{
                width: '50px',
                '& .MuiOutlinedInput-root': {
                  height: '32px',
                  '& fieldset': {
                    borderColor: '#ddd',
                  },
                }
              }}
            />
            <SoftTypography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: '14px', ml: 0.5, whiteSpace: 'nowrap' }}
            >
              / {totalPages}
            </SoftTypography>

          </Box>

          <SoftPagination
            item
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            sx={{
              minWidth: '32px',
              height: '32px',
              fontSize: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              '&:not(:disabled):hover': {
                bgcolor: '#f8f9fa'
              }
            }}
          >
            <Icon sx={{ fontSize: 16 }}>chevron_right</Icon>
          </SoftPagination>

          <SoftPagination
            item
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            sx={{
              minWidth: '32px',
              height: '32px',
              fontSize: '12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              '&:not(:disabled):hover': {
                bgcolor: '#f8f9fa'
              }
            }}
          >
            <Icon sx={{ fontSize: 16 }}>last_page</Icon>
          </SoftPagination>
        </Box>
      </Box>

      {/* Main Table */}
      <Card sx={{
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <SoftBox p={2}>
          <SoftTypography variant="h6" fontWeight="600" gutterBottom sx={{ color: '#333', mb: 2, fontSize: '16px' }}>
            All Surveys Hits
          </SoftTypography>

          {loading ? (
            <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="400px">
              {/* <CircularProgress color="primary" size={50} />
              <SoftTypography variant="body2" color="text" mt={2} fontWeight="500" fontSize="14px">
                Loading data...
              </SoftTypography> */}
              <FlagLoader />
            </SoftBox>
          ) : hitsData.length > 0 ? (
            <Table
              columns={tableColumns}
              rows={hitsData}
              sx={{
                '& .MuiTableRow-root:hover': {
                  bgcolor: 'rgba(0,0,0,0.02)'
                },
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid #f0f0f0',
                  verticalAlign: 'middle',
                  py: 1.5,
                  fontSize: '14px'
                },
                '& .MuiTableHead-root': {
                  '& .MuiTableCell-root': {
                    fontWeight: '600',
                    color: '#444',
                    fontSize: '14px',
                    borderBottom: '2px solid #e0e0e0',
                    py: 1.5
                  }
                }
              }}
            />
          ) : (
            <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="400px">
              <Icon sx={{ fontSize: 50, color: '#ddd', mb: 1 }}>bar_chart</Icon>
              <SoftTypography variant="body2" color="textSecondary" gutterBottom fontSize="14px">
                No data found
              </SoftTypography>
              <SoftTypography variant="caption" color="textSecondary" fontSize="12px">
                Try different search criteria
              </SoftTypography>
            </SoftBox>
          )}
        </SoftBox>
      </Card>

      <Footer />
    </DashboardLayout>
  );
}

export default StudiesHits;
